/**
 * 把"流式接收 chunk"和"逐字渲染"的节奏解耦：
 *   上游 PikeRag SSE 的 content chunk 大小不均（一会儿 3 个字、一会儿 12 个字），
 *   直接渲染会"一坨一坨"地蹦字。这里用队列 + requestAnimationFrame 平滑成
 *   逐字打字机效果；落后越多每帧吐越多，避免"上游已答完前端还在慢悠悠打字"。
 *
 * 用法：
 *   const tw = createStreamTypewriter({ onTick: (visible) => render(visible) });
 *   sse.onContent = (delta) => tw.push(delta);
 *   // 流结束 → 立刻吐完剩余，让 visible 与最终文本同步
 *   await sse; tw.flush();
 *   // 用户 abort → 放弃队列里还没显示的字符
 *   tw.cancel();
 */
type StreamTypewriterOptions = {
  /** 文本变化时回调，参数为当前应显示的完整文本 */
  onTick: (visible: string) => void;
  /** 每帧吐出的字符数，默认 2 */
  charsPerFrame?: number;
  /** pending 队列超过此阈值则立即一次性吐完，避免无限延迟 */
  flushThreshold?: number;
  /**
   * 是否启用逐字打字机平滑。默认 true（保持既有行为）。
   * 传 false 时退化为"即时模式"：每次 push 直接把 delta 追加并回调完整文本，
   * 不经过 requestAnimationFrame 排队，按上游到达节奏原速渲染。
   */
  enabled?: boolean;
};

export type StreamTypewriter = {
  push: (delta: string) => void;
  flush: () => void;
  cancel: () => void;
};

export function createStreamTypewriter(opts: StreamTypewriterOptions): StreamTypewriter {
  const charsPerFrame = Math.max(1, opts.charsPerFrame ?? 2);
  const flushThreshold = Math.max(charsPerFrame, opts.flushThreshold ?? 400);
  const enabled = opts.enabled ?? true;
  let visible = "";
  let pending: string[] = [];
  let rafId: number | null = null;

  // 即时模式：关闭打字机平滑，直接累积并回调，按上游节奏原速渲染。
  if (!enabled) {
    return {
      push(delta: string) {
        if (!delta) return;
        visible += delta;
        opts.onTick(visible);
      },
      flush() {
        /* 即时模式无队列，无需收尾 */
      },
      cancel() {
        /* 即时模式无队列，无需取消 */
      },
    };
  }

  const tick = () => {
    rafId = null;
    if (pending.length === 0) return;
    const step = Math.min(pending.length, charsPerFrame);
    visible += pending.slice(0, step).join("");
    pending = pending.slice(step);
    opts.onTick(visible);
    if (pending.length > 0) rafId = requestAnimationFrame(tick);
  };

  return {
    push(delta: string) {
      if (!delta) return;
      // Array.from 按 code point 切，避免劈开 emoji / 代理对
      pending = pending.concat(Array.from(delta));
      // 积压过多则立即一次性吐完，避免上游早已答完前端还在慢慢打字
      if (pending.length > flushThreshold) {
        if (rafId != null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        visible += pending.join("");
        pending = [];
        opts.onTick(visible);
        return;
      }
      if (rafId == null) rafId = requestAnimationFrame(tick);
    },
    flush() {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (pending.length === 0) return;
      visible += pending.join("");
      pending = [];
      opts.onTick(visible);
    },
    cancel() {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pending = [];
    },
  };
}
